import { useCallback, useMemo, useState } from 'react';
import { useSpot } from 'framework';

interface RetailerFileUploadOptions {
  slug: string;
  fileTag: string;
  defaultExt?: string;
}

interface RetailerFileQueryResult {
  url: string;
  cdnUrl: string;
}

export function useRetailerFileUpload(options: RetailerFileUploadOptions) {
  const { slug, fileTag, defaultExt } = options;

  const { raw } = useSpot();
  const [data, setData] = useState<RetailerFileQueryResult>();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error>();

  // first, add the file to retailer with the name we want to use
  // then, put the file to the returned URL
  const put = useCallback(async (file: File) => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const ext = getFileExtension(file.name) ?? defaultExt;

      if (!ext) {
        throw new Error(`File extension not found for file: ${file.name}`);
      }

      const path = `retailer/${slug}/file-upload-url/${fileTag}.${ext}`;
      const urlResult = await raw<RetailerFileQueryResult>(path);

      if (!urlResult) {
        throw new Error('Failed to add file reference to retailer file upload');
      }

      const { url, cdnUrl } = urlResult;
      const uploadResult = await fetch(url, { method: 'PUT', body: file });

      if (!uploadResult.ok) {
        throw new Error('Failed to upload retailer file');
      }

      setData({ url, cdnUrl });

      return { url, cdnUrl };
    } catch (error) {
      setError(error as Error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { data, put, isLoading, error };
}

function getFileExtension(fileName: string): string | undefined {
  const fileExtensionRegex = /(?:\.([^.]+))?$/;
  const extension = fileExtensionRegex.exec(fileName)?.[1];

  return extension;
}
